import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Users, RefreshCw, Download } from "lucide-react";

const RANGES = [
  { d: 7,  label: "7d" },
  { d: 14, label: "14d" },
  { d: 30, label: "30d" },
  { d: 60, label: "60d" },
  { d: 90, label: "90d" },
];

function fmtShort(iso) {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

export default function DailySignupsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeTest, setIncludeTest] = useState(false);

  const load = async (d = days, inc = includeTest) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/signups/daily`, { params: { days: d, include_test: inc } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load daily signups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days, includeTest); /* eslint-disable-next-line */ }, [days, includeTest]);

  const max = useMemo(() => Math.max(1, ...((data?.daily || []).map((x) => x.count))), [data]);
  const todayCount = useMemo(() => (data?.daily?.[data.daily.length - 1]?.count) || 0, [data]);
  const avg = useMemo(() => {
    if (!data?.daily?.length) return 0;
    return data.total / data.daily.length;
  }, [data]);

  const exportCsv = () => {
    if (!data?.daily) return;
    const rows = [["date", "signups"], ...data.daily.map((d) => [d.date, d.count])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qualmitta_signups_${days}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="daily-signups-panel" className="border border-slate-200 bg-white">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4">
        <div>
          <div className="overline mb-1 text-[#2563EB]">DAILY SIGNUPS</div>
          <div className="flex items-baseline gap-3">
            <div className="font-serif text-3xl font-semibold text-slate-900">
              {data?.total ?? 0}
            </div>
            <div className="text-sm text-slate-500">signups · last {days} days</div>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            <span className="text-slate-800 font-medium">{todayCount}</span> today
            <span className="mx-2 text-slate-300">·</span>
            <span className="text-slate-800 font-medium">{avg.toFixed(1)}</span> /day avg
            {data?.excluded_test_accounts > 0 && !includeTest && (
              <span className="ml-2 text-amber-300/80">
                · {data.excluded_test_accounts} test account{data.excluded_test_accounts === 1 ? "" : "s"} hidden
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="mr-1 flex items-center gap-1.5 text-[11px] text-slate-500" title="Include @example.com and dev accounts">
            <input
              data-testid="signups-include-test"
              type="checkbox"
              checked={includeTest}
              onChange={(e) => setIncludeTest(e.target.checked)}
              className="accent-[#2563EB]"
            />
            include test data
          </label>
          <div className="flex overflow-hidden border border-slate-200">
            {RANGES.map((r) => (
              <button
                key={r.d}
                data-testid={`signups-range-${r.d}`}
                onClick={() => setDays(r.d)}
                className={`px-3 py-1.5 text-xs font-medium tracking-wide transition-colors ${
                  days === r.d
                    ? "bg-[#2563EB] text-white"
                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            data-testid="signups-refresh"
            onClick={() => load(days)}
            className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-300 hover:text-slate-900"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            data-testid="signups-export"
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-300 hover:text-slate-900"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
        </div>
      </div>

      <div className="p-6">
        {!data ? (
          <div className="text-center text-xs text-slate-400">Loading…</div>
        ) : data.daily.length === 0 ? (
          <div className="text-center text-sm text-slate-500">No signups in window.</div>
        ) : (
          <div className="flex items-end gap-[2px]" data-testid="signups-bars" style={{ height: 180 }}>
            {data.daily.map((d, i) => {
              const h = Math.max(2, Math.round((d.count / max) * 170));
              const isToday = i === data.daily.length - 1;
              return (
                <div key={d.date} className="group relative flex flex-1 flex-col items-center justify-end">
                  <div
                    className={`w-full transition-colors ${
                      isToday ? "bg-[#2563EB]" : d.count === 0 ? "bg-slate-50" : "bg-[#2563EB]/60 hover:bg-[#2563EB]"
                    }`}
                    style={{ height: h }}
                    title={`${d.date} · ${d.count} signup${d.count === 1 ? "" : "s"}`}
                    data-testid={i === data.daily.length - 1 ? "signups-bar-today" : undefined}
                  />
                  {/* Tooltip on hover */}
                  <div className="pointer-events-none absolute -top-9 hidden whitespace-nowrap border border-slate-200 bg-slate-900 px-2 py-1 text-[10px] font-medium text-white group-hover:block">
                    {fmtShort(d.date)} · {d.count}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {data?.daily?.length ? (
          <div className="mt-3 flex justify-between text-[10px] uppercase tracking-wider text-slate-400">
            <span>{fmtShort(data.daily[0].date)}</span>
            {data.daily.length > 6 && (
              <span>{fmtShort(data.daily[Math.floor(data.daily.length / 2)].date)}</span>
            )}
            <span>{fmtShort(data.daily[data.daily.length - 1].date)} (today)</span>
          </div>
        ) : null}
      </div>

      {/* Signups feed */}
      <div className="border-t border-slate-200">
        <div className="flex items-center gap-2 border-b border-slate-200 bg-[#FAFAF7] px-5 py-2">
          <Users className="h-3.5 w-3.5 text-slate-500" />
          <span className="overline text-slate-500">SIGNUPS IN WINDOW</span>
          <span className="ml-auto text-[10px] text-slate-400">newest first</span>
        </div>
        <div className="max-h-[300px] overflow-y-auto" data-testid="signups-feed">
          {(data?.signups || []).length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400">No signups in window.</div>
          ) : (
            [...(data?.signups || [])].reverse().map((u, i) => (
              <div key={i} className="flex items-center justify-between border-b border-slate-100 px-5 py-2 text-sm">
                <div className="text-slate-800">{u.email}</div>
                <div className="text-right text-[11px] text-slate-400">
                  {(u.created_at || "").replace("T", " ").slice(0, 19)}
                  <div className="text-[9px] uppercase text-slate-300">{u.auth_method || "email"}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
